import {useRef, useState} from 'react';
import axios from 'axios';
import {useDispatch} from 'react-redux';
import {QUIKLEARN_BACKEND_URL} from 'config/app.config';
import {showQuotaPaywall} from 'store/subscriptionUi';
import {getWebAccessToken} from 'utils/webAuthSession';

const useFetchTokenLiveKit = () => {
  const dispatch = useDispatch();
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const quotaBlockedRef = useRef(false);

  /**
   * Fetch a LiveKit token.
   *
   * IMPORTANT: This function is intentionally **non-throwing**. It returns `null`
   * on failure and sets `error` state instead.
   *
   * Reason: historically, callers didn't consistently `.catch()` token fetch
   * failures, which can surface as disruptive overlays/unhandled promise
   * rejections in the web app when the token service is down.
   */
  const getToken = async ({
    teacherName,
    courseName,
    voice_id,
    courseId,
    lessonId,
    session_id,
    lastQuestion,
    mode = 'VOICE',
  }) => {
    setLoading(true);
    quotaBlockedRef.current = false;
    try {
      const accessToken = getWebAccessToken();
      if (!accessToken) throw new Error('Your web learning session has expired. Please reopen the course.');
      const response = await axios.post(
        `${QUIKLEARN_BACKEND_URL}/api/user/tutor-sessions/token`,
        {
          mode: String(mode || 'VOICE').toUpperCase(),
          clientRequestId: session_id,
          instructor: teacherName,
          voiceId: voice_id,
          institution: 'quiklearn',
          courseId,
          lessonId: lessonId ?? null,
          conversationSessionId: session_id,
          lastQuestion,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const nextToken = response?.data?.data?.token || response?.data?.token || null;
      setToken(nextToken);
      setError(null);
      return nextToken;
    } catch (err) {
      if (err?.response?.status === 402) {
        quotaBlockedRef.current = true;
        const detail = err.response?.data?.details || err.response?.data?.data || {};
        dispatch(showQuotaPaywall({
          meter: detail.meter || String(mode || 'VOICE').toLowerCase(),
          planKey: detail.planKey || null,
          allowed: detail.allowed ?? null,
          used: detail.used ?? null,
          remaining: detail.remaining ?? 0,
          resetAt: detail.resetAt || null,
          message:
            detail.message ||
            err.response?.data?.message ||
            'Your tutor allowance is exhausted.',
        }));
      }
      console.error('Tutor token request failed', {status: err?.response?.status || null});
      setError(err?.message || 'An error occurred');
      setToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const clearToken = () => {
    setToken(null);
    setError(null);
  };

  return {
    token,
    error,
    loading,
    getToken,
    clearToken,
    wasQuotaBlocked: () => quotaBlockedRef.current,
  };
};

export default useFetchTokenLiveKit;
